import { createFileRoute, Link } from "@tanstack/react-router";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import { useOrganization } from "@/hooks/use-organization";
import { useRolePermissions, Permission } from "@/hooks/use-role-permissions";
import { useOrgPath } from "@/hooks/useOrgPath";

type Role = {
  id: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  permissions: Permission[];
};

// BU-scoped permissions only (excluding global-only and legacy permissions)
const _BU_PERMISSIONS = [
  Permission.ManageJobPosts,
  Permission.ViewJobPosts,
  Permission.ManageChat,
  Permission.ViewChat,
  Permission.ManageJobOffers,
  Permission.ViewJobOffers,
  Permission.ApproveTimesheets,
  Permission.ViewTimesheets,
  Permission.ManageInvoices,
  Permission.ViewInvoices,
  Permission.ManageProviderPools,
  Permission.ViewProviderPools,
  Permission.ManageAffiliations,
  Permission.ManageTeamMembers,
  Permission.ViewTeamMembers,
  Permission.ManageBuSettings,
] as const;

// Permission groups for better UI organization
const PERMISSION_GROUPS = {
  "Job Management": [
    Permission.ManageJobPosts,
    Permission.ViewJobPosts,
    Permission.ManageJobOffers,
    Permission.ViewJobOffers,
  ],
  Communication: [Permission.ManageChat, Permission.ViewChat],
  "Timesheets & Invoices": [
    Permission.ApproveTimesheets,
    Permission.ViewTimesheets,
    Permission.ManageInvoices,
    Permission.ViewInvoices,
  ],
  "Provider Pools": [
    Permission.ManageProviderPools,
    Permission.ViewProviderPools,
  ],
  "Hired Providers": [Permission.ManageAffiliations],
  Team: [
    Permission.ManageTeamMembers,
    Permission.ViewTeamMembers,
    Permission.ManageBuSettings,
  ],
} as const;

function formatPermissionName(permission: Permission): string {
  return permission
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function RolesPage() {
  const { user } = Route.useRouteContext();
  const { selectedOrgBusinessUnit } = useOrganization();
  const { hasPermission } = useRolePermissions(user);
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const orgPath = useOrgPath();

  const canManageRoles = hasPermission(Permission.ManageBuSettings);
  const providerSearchEnabled =
    selectedOrgBusinessUnit.enableProviderSearch ?? false;
  const getVisibleRolePermissions = (permissions: Permission[]) =>
    user?.is_admin || providerSearchEnabled
      ? permissions
      : permissions.filter(
          (permission) => permission !== Permission.ProviderSearch,
        );

  const permissionGroups: Record<string, readonly Permission[]> =
    providerSearchEnabled
      ? {
          ...PERMISSION_GROUPS,
          "Job Management": [
            ...PERMISSION_GROUPS["Job Management"],
            Permission.ProviderSearch,
          ],
        }
      : PERMISSION_GROUPS;

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);

  // Create/Edit form state
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formPermissions, setFormPermissions] = useState<Permission[]>([]);

  const { data: roles = [], isLoading: rolesLoading } = useQuery(
    trpc.organization.getBuRoles.queryOptions({
      organizationBusinessUnitId: selectedOrgBusinessUnit.id,
    }),
  );

  const createRoleMutation = useMutation(
    trpc.organization.createBuRole.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          trpc.organization.getBuRoles.queryOptions({
            organizationBusinessUnitId: selectedOrgBusinessUnit.id,
          }),
        );
        setIsCreateDialogOpen(false);
        resetForm();
      },
    }),
  );

  const updateRoleMutation = useMutation(
    trpc.organization.updateBuRole.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          trpc.organization.getBuRoles.queryOptions({
            organizationBusinessUnitId: selectedOrgBusinessUnit.id,
          }),
        );
        setIsEditDialogOpen(false);
        setSelectedRole(null);
        resetForm();
      },
    }),
  );

  const deleteRoleMutation = useMutation(
    trpc.organization.deleteBuRole.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          trpc.organization.getBuRoles.queryOptions({
            organizationBusinessUnitId: selectedOrgBusinessUnit.id,
          }),
        );
        setIsDeleteDialogOpen(false);
        setSelectedRole(null);
      },
    }),
  );

  const resetForm = () => {
    setFormName("");
    setFormDescription("");
    setFormPermissions([]);
  };

  const handleCreateClick = () => {
    resetForm();
    setIsCreateDialogOpen(true);
  };

  const handleEditClick = (role: Role) => {
    setSelectedRole(role);
    setFormName(role.name);
    setFormDescription(role.description || "");
    setFormPermissions(role.permissions);
    setIsEditDialogOpen(true);
  };

  const handleDeleteClick = (role: Role) => {
    setSelectedRole(role);
    setIsDeleteDialogOpen(true);
  };

  const handleCreateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formName.trim()) {
      return;
    }

    createRoleMutation.mutate({
      organizationBusinessUnitId: selectedOrgBusinessUnit.id,
      name: formName.trim(),
      description: formDescription.trim() || undefined,
      permissions: formPermissions,
    });
  };

  const handleUpdateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedRole || !formName.trim()) {
      return;
    }

    updateRoleMutation.mutate({
      organizationBusinessUnitId: selectedOrgBusinessUnit.id,
      roleId: selectedRole.id,
      name: formName.trim(),
      description: formDescription.trim() || null,
      permissions: formPermissions,
    });
  };

  const handleConfirmDelete = () => {
    if (!selectedRole) {
      return;
    }

    deleteRoleMutation.mutate({
      organizationBusinessUnitId: selectedOrgBusinessUnit.id,
      roleId: selectedRole.id,
    });
  };

  const togglePermission = (permission: Permission) => {
    setFormPermissions((prev) =>
      prev.includes(permission)
        ? prev.filter((p) => p !== permission)
        : [...prev, permission],
    );
  };

  // If user doesn't have permission to manage roles, show access denied
  if (!canManageRoles) {
    return (
      <div className="container mx-auto py-8 px-2">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col items-center justify-center py-12 px-4 text-gray-500 bg-gray-50 rounded-lg">
            <svg
              className="w-12 h-12 mb-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
            <h3 className="text-lg font-semibold mb-2">Access Denied</h3>
            <p className="text-sm text-center">
              You don't have permission to manage roles.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-2">
      <div className="max-w-5xl mx-auto">
        <div className="mb-4">
          <Link
            to={orgPath("/team")}
            className={buttonVariants({ variant: "ghost", size: "sm" })}
          >
            ← Back to Team
          </Link>
        </div>

        <div className="mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-semibold">
                {selectedOrgBusinessUnit.name}
              </h1>
              <h2 className="text-lg text-gray-600 mt-1">Roles</h2>
            </div>
            <Dialog
              open={isCreateDialogOpen}
              onOpenChange={setIsCreateDialogOpen}
            >
              <DialogTrigger asChild>
                <Button onClick={handleCreateClick}>Create Role</Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create Role</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateSubmit} className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium mb-1.5 block">
                      Role Name
                    </Label>
                    <Input
                      value={formName}
                      onChange={(e) => setFormName(e.target.value)}
                      placeholder="Enter role name"
                      maxLength={100}
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium mb-1.5 block">
                      Description (Optional)
                    </Label>
                    <Textarea
                      value={formDescription}
                      onChange={(e) => setFormDescription(e.target.value)}
                      placeholder="Enter role description"
                      rows={3}
                      maxLength={500}
                      className="resize-none"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium mb-3 block">
                      Permissions
                    </Label>
                    <div className="space-y-4">
                      {Object.entries(permissionGroups).map(
                        ([groupName, permissions]) => (
                          <div key={groupName}>
                            <h4 className="text-sm font-medium mb-2 text-gray-700">
                              {groupName}
                            </h4>
                            <div className="space-y-2 ml-2">
                              {permissions.map((permission) => (
                                <div
                                  key={permission}
                                  className="flex items-center space-x-2"
                                >
                                  <Checkbox
                                    id={`create-${permission}`}
                                    checked={formPermissions.includes(
                                      permission,
                                    )}
                                    onCheckedChange={() =>
                                      togglePermission(permission)
                                    }
                                  />
                                  <label
                                    htmlFor={`create-${permission}`}
                                    className="text-sm cursor-pointer"
                                  >
                                    {formatPermissionName(permission)}
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 sm:justify-end pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsCreateDialogOpen(false)}
                      className="w-full sm:w-auto"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={
                        createRoleMutation.isPending || !formName.trim()
                      }
                      className="w-full sm:w-auto"
                    >
                      {createRoleMutation.isPending
                        ? "Creating..."
                        : "Create Role"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {rolesLoading ? (
          <Card className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Permissions</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[...Array(3)].map((_, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Skeleton className="h-4 w-32" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-48" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-64" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-8 w-24" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        ) : !roles || roles.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-gray-500 bg-gray-50 rounded-lg">
            <svg
              className="w-12 h-12 mb-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
              />
            </svg>
            <h3 className="text-lg font-semibold mb-2">No Roles Yet</h3>
            <p className="text-sm text-center mb-4">
              Get started by creating a role for your team
            </p>
            <Button onClick={handleCreateClick} variant="outline" size="sm">
              Create Role
            </Button>
          </div>
        ) : (
          <Card className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Permissions</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {roles.map((role: Role) => {
                  const visiblePermissions = getVisibleRolePermissions(
                    role.permissions,
                  );

                  return (
                    <TableRow key={role.id}>
                      <TableCell>
                        <span className="font-medium">{role.name}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-gray-600">
                          {role.description || "—"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {visiblePermissions.length === 0 ? (
                            <span className="text-sm text-gray-400">
                              No permissions
                            </span>
                          ) : (
                            visiblePermissions.map((permission) => (
                              <Badge
                                key={permission}
                                variant="outline"
                                className="text-xs"
                              >
                                {formatPermissionName(permission)}
                              </Badge>
                            ))
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditClick(role)}
                          >
                            Edit
                          </Button>
                          {!role.isDefault && (
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDeleteClick(role)}
                            >
                              Delete
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        )}

        {/* Edit Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Role</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateSubmit} className="space-y-4">
              <div>
                <Label className="text-sm font-medium mb-1.5 block">
                  Role Name
                </Label>
                <Input
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="Enter role name"
                  maxLength={100}
                  required
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">
                  Description (Optional)
                </Label>
                <Textarea
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  placeholder="Enter role description"
                  rows={3}
                  maxLength={500}
                  className="resize-none"
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-3 block">
                  Permissions
                </Label>
                <div className="space-y-4">
                  {Object.entries(permissionGroups).map(
                    ([groupName, permissions]) => (
                      <div key={groupName}>
                        <h4 className="text-sm font-medium mb-2 text-gray-700">
                          {groupName}
                        </h4>
                        <div className="space-y-2 ml-2">
                          {permissions.map((permission) => (
                            <div
                              key={permission}
                              className="flex items-center space-x-2"
                            >
                              <Checkbox
                                id={`edit-${permission}`}
                                checked={formPermissions.includes(permission)}
                                onCheckedChange={() =>
                                  togglePermission(permission)
                                }
                              />
                              <label
                                htmlFor={`edit-${permission}`}
                                className="text-sm cursor-pointer"
                              >
                                {formatPermissionName(permission)}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    ),
                  )}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:justify-end pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsEditDialogOpen(false)}
                  className="w-full sm:w-auto"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={updateRoleMutation.isPending || !formName.trim()}
                  className="w-full sm:w-auto"
                >
                  {updateRoleMutation.isPending ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Role</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p>
                Are you sure you want to delete the role{" "}
                <strong>{selectedRole?.name}</strong>? This action cannot be
                undone.
              </p>
              {selectedRole?.permissions &&
                getVisibleRolePermissions(selectedRole.permissions).length >
                  0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                    <p className="text-sm text-yellow-800">
                      This role has{" "}
                      {
                        getVisibleRolePermissions(selectedRole.permissions)
                          .length
                      }{" "}
                      permission
                      {getVisibleRolePermissions(selectedRole.permissions)
                        .length !== 1
                        ? "s"
                        : ""}{" "}
                      assigned to it.
                    </p>
                  </div>
                )}
              <div className="flex justify-end gap-4">
                <Button
                  variant="outline"
                  onClick={() => setIsDeleteDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleConfirmDelete}
                  disabled={deleteRoleMutation.isPending}
                >
                  {deleteRoleMutation.isPending ? "Deleting..." : "Delete"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

export const Route = createFileRoute(
  "/_org/org/bu/$organizationBusinessUnitId/team/roles/",
)({
  component: RolesPage,
});
